import { useMemo } from "react";

import {
  DetailsRow,
  IDetailsRowProps,
  SelectionMode,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  TextBadge,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { SoftwarePackage } from "@libs/api/gateways/field/field-ops-gateway.dtos";

import { getTypeBadgeColor } from "../utils";

export interface SoftwareSearchTableProps extends ShimmeredDetailsListProps {
  onOpenVersions: React.Dispatch<React.SetStateAction<string>>;
}

export const SoftwareSearchTable = ({
  onOpenVersions,
  ...props
}: SoftwareSearchTableProps) => {
  const theme = useTheme();

  const columns: DataTableColumn<SoftwarePackage>[] = useMemo(() => {
    return [
      {
        key: "softwarePackageType",
        name: "Type",
        minWidth: 100,
        maxWidth: 100,
        isResizable: true,
        sort: true,
        onRender: (softwarePackage: SoftwarePackage) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <TextBadge
              badgeColor={getTypeBadgeColor(
                softwarePackage.softwarePackageType
              )}
              badgeSize={TextBadgeSize.small}
              styles={{ root: { width: "fit-content" } }}
            >
              {softwarePackage.softwarePackageType ?? "--"}
            </TextBadge>
          </Stack>
        )
      },
      {
        key: "displayName",
        name: "Name",
        minWidth: 150,
        maxWidth: 300,
        isResizable: true,
        isSorted: true,
        sort: true,
        onRender: (softwarePackage: SoftwarePackage) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <Text>{softwarePackage.displayName}</Text>
          </Stack>
        )
      },
      {
        key: "code",
        name: "Code",
        minWidth: 300,
        maxWidth: 300,
        isResizable: true,
        sort: true,
        onRender: (softwarePackage: SoftwarePackage) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <Text>{softwarePackage.code}</Text>
          </Stack>
        )
      },
      {
        key: "description",
        name: "Description",
        minWidth: 150,
        maxWidth: 300,
        isResizable: true,
        sort: true,
        onRender: (softwarePackage: SoftwarePackage) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <Text>{softwarePackage.description}</Text>
          </Stack>
        )
      }
    ];
  }, []);

  return (
    <DataTable
      stickyHeader
      shimmerLines={10}
      columns={columns}
      selectionMode={SelectionMode.none}
      onRenderRow={row =>
        row ? (
          <Stack
            onClick={() => onOpenVersions((row.item as SoftwarePackage).id)}
            styles={{
              root: {
                "&:hover": {
                  backgroundColor:
                    theme.semanticColors.listItemBackgroundHovered,
                  cursor: "pointer"
                }
              }
            }}
          >
            <DetailsRow {...(row as IDetailsRowProps)} />
          </Stack>
        ) : null
      }
      onShouldVirtualize={() => true}
      detailsListStyles={{
        contentWrapper: {
          ":hover": {
            background: theme.palette.neutralQuaternaryAlt
          }
        }
      }}
      {...props}
    />
  );
};
